import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Card, Select, DatePicker } from 'antd';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { fetchExpenses, createExpense, updateExpense, deleteExpense, type ExpenseDto } from '../services/cashService';
import { fetchExpenseTypes, type ExpenseTypeDto } from '../services/cashService';
import { fetchCashRegisters, type CashRegisterDto } from '../services/cashService';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

interface Branch {
  id: number;
  branchName: string;
}

const Expenses: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [expenses, setExpenses] = useState<ExpenseDto[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<ExpenseTypeDto[]>([]);
  const [cashRegisters, setCashRegisters] = useState<CashRegisterDto[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseDto | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [form] = Form.useForm();

  const filteredExpenses = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return expenses;
    const [start, end] = dateRange;
    const startStr = start.startOf('day').format('YYYY-MM-DD');
    const endStr = end.endOf('day').format('YYYY-MM-DD');
    return expenses.filter((e) => {
      const d = dayjs(e.expenseDate).format('YYYY-MM-DD');
      return d >= startStr && d <= endStr;
    });
  }, [expenses, dateRange]);

  useEffect(() => {
    loadExpenses();
    loadExpenseTypes();
    loadCashRegisters();
    loadBranches();
  }, []);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const data = await fetchExpenses();
      setExpenses(data);
    } catch (error) {
      message.error('Gider verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadExpenseTypes = async () => {
    try {
      const data = await fetchExpenseTypes();
      setExpenseTypes(data);
    } catch (error) {
      console.error('Gider tipleri yüklenirken hata:', error);
    }
  };

  const loadCashRegisters = async () => {
    try {
      const data = await fetchCashRegisters();
      setCashRegisters(data);
    } catch (error) {
      console.error('Kasalar yüklenirken hata:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const { data } = await api.get<Branch[]>('/branches');
      setBranches(data);
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  };

  const handleAdd = () => {
    setEditingExpense(null);
    form.resetFields();
    form.setFieldsValue({ expenseDate: dayjs() });
    setModalVisible(true);
  };

  const handleEdit = (record: ExpenseDto) => {
    setEditingExpense(record);
    form.setFieldsValue({
      expenseNo: record.expenseNo,
      expenseDate: dayjs(record.expenseDate),
      expenseTypeId: record.expenseTypeId,
      cashRegisterId: record.cashRegisterId,
      amount: record.amount,
      description: record.description,
      receiptNo: record.receiptNo,
      branchId: record.branchId});
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteExpense(id);
      message.success('Gider başarıyla silindi');
      loadExpenses();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Gider silinirken hata oluştu');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const expenseDate = values.expenseDate && (typeof values.expenseDate === 'string'
        ? values.expenseDate
        : values.expenseDate.format?.('YYYY-MM-DD'));
      if (!expenseDate) {
        message.error('Lütfen gider tarihi seçin');
        return;
      }
      const payload = {
        expenseDate,
        expenseTypeId: Number(values.expenseTypeId),
        cashRegisterId: Number(values.cashRegisterId),
        amount: Number(values.amount),
        description: values.description?.trim() || undefined,
        receiptNo: values.receiptNo?.trim() || undefined,
        branchId: values.branchId != null && values.branchId !== '' ? Number(values.branchId) : undefined};
      if (editingExpense) {
        await updateExpense(editingExpense.id, { ...payload, expenseDate: payload.expenseDate as string });
        message.success('Gider başarıyla güncellendi');
      } else {
        await createExpense(payload);
        message.success('Gider başarıyla eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      loadExpenses();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      const msg = error.response?.data?.message
        ?? (typeof error.response?.data === 'string' ? error.response?.data : null)
        ?? 'Gider kaydedilirken hata oluştu';
      message.error(msg);
    }
  };

  const columns = [
    {
      title: 'Gider No',
      dataIndex: 'expenseNo',
      key: 'expenseNo'},
    {
      title: 'Tarih',
      dataIndex: 'expenseDate',
      key: 'expenseDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY')},
    {
      title: 'Gider Tipi',
      dataIndex: 'expenseTypeId',
      key: 'expenseTypeId',
      render: (expenseTypeId: number) => {
        const expenseType = expenseTypes.find(et => et.id === expenseTypeId);
        return expenseType ? expenseType.typeName : '-';
      }},
    {
      title: 'Kasa',
      dataIndex: 'cashRegisterId',
      key: 'cashRegisterId',
      render: (cashRegisterId: number) => {
        const cashRegister = cashRegisters.find(cr => cr.id === cashRegisterId);
        return cashRegister ? cashRegister.registerName : '-';
      }},
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span style={{ color: 'red', fontWeight: 'bold' }}>
          -{amount.toFixed(2)} ₺
        </span>
      )},
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description'},
    {
      title: 'Fiş No',
      dataIndex: 'receiptNo',
      key: 'receiptNo'},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ExpenseDto) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDelete && (
          <Popconfirm
            title="Bu gideri silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
          )}
        </Space>
      )},
  ];

  return (
    <div>
      <Card
        title="Gider Yönetimi"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Yeni Gider Ekle
          </Button>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <Space wrap>
            <FilterOutlined />
            <DatePicker.RangePicker
              format="DD.MM.YYYY"
              placeholder={['Başlangıç', 'Bitiş']}
              value={dateRange ?? undefined}
              onChange={(dates) => setDateRange(dates ?? null)}
            />
            {dateRange && (
              <Button onClick={() => setDateRange(null)}>
                Filtreyi temizle
              </Button>
            )}
          </Space>
        </Space>
        <Table
          columns={columns}
          dataSource={filteredExpenses}
          rowKey="id"
          loading={loading}
          scroll={{ x: 700 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} kayıt${dateRange ? ' (filtrelenmiş)' : ''}`}}
        />
      </Card>

      <Modal
        title={editingExpense ? 'Gideri Düzenle' : 'Yeni Gider Ekle'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="expenseDate"
            label="Gider Tarihi"
            rules={[{ required: true, message: 'Lütfen gider tarihi seçin' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item
            name="expenseTypeId"
            label="Gider Tipi"
            rules={[{ required: true, message: 'Lütfen gider tipi seçin' }]}
          >
            <Select placeholder="Gider tipi seçin">
              {expenseTypes.map(et => (
                <Select.Option key={et.id} value={et.id}>
                  {et.typeName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="cashRegisterId"
            label="Kasa"
            rules={[{ required: true, message: 'Lütfen kasa seçin' }]}
          >
            <Select placeholder="Kasa seçin">
              {cashRegisters.map(cr => (
                <Select.Option key={cr.id} value={cr.id}>
                  {cr.registerName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Tutar"
            rules={[{ required: true, message: 'Lütfen tutar girin' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              min={0}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea rows={3} placeholder="Gider açıklaması" />
          </Form.Item>

          <Form.Item
            name="receiptNo"
            label="Fiş/Fatura No"
          >
            <Input placeholder="Fiş veya fatura numarası" />
          </Form.Item>

          <Form.Item
            name="branchId"
            label="Şube"
          >
            <Select placeholder="Şube seçin (opsiyonel)" allowClear>
              {branches.map(branch => (
                <Select.Option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Expenses;

